// Dependency Inversion: this context acts as the DI container for auth state.
// Components consume auth state via useAuth(), never reading from Firebase directly.

import { createContext, useEffect, useState, type ReactNode } from 'react'
import { onAuthStateChanged, type User } from 'firebase/auth'
import { auth } from '@/lib/firebase/auth'
import { syncAdminClaimsFn } from '@/lib/firebase/functions'
import { ensureUserProfile } from '@/features/auth/services/userProfile.service'

interface AuthContextValue {
  user: User | null
  loading: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let isActive = true
    let unsubscribe: (() => void) | null = null

    const subscribeToAuth = () => {
      if (!isActive) {
        return
      }

      unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
        setUser(firebaseUser)
        if (!firebaseUser) {
          setLoading(false)
          return
        }

        setLoading(true)
        void ensureUserProfile(firebaseUser)
            .catch((err) => {
              console.error('Failed to upsert user profile:', err)
            })
            .then(() => syncAdminClaimsFn())
            .then(() => firebaseUser.getIdToken(true))
            .catch((err) => {
              console.error('Failed to refresh authorization claims:', err)
            })
            .finally(() => setLoading(false))
      })
    }

    const devImpersonation =
      import.meta.env.DEV && __DEV_IMPERSONATION_ENABLED__
        ? import('@/lib/firebase/devImpersonation').then(({ startDevImpersonation }) =>
            startDevImpersonation()
          )
        : Promise.resolve()

    void devImpersonation
      .catch((err: unknown) => {
        console.error('Development impersonation failed:', err)
      })
      .finally(subscribeToAuth)

    return () => {
      isActive = false
      unsubscribe?.()
    }
  }, [])

  return <AuthContext.Provider value={{ user, loading }}>{children}</AuthContext.Provider>
}

export { AuthContext, AuthProvider }
